import * as openpgp from "openpgp";

// Mirror KLUCZY TREŚCI — jedno miejsce, z którego utile deszyfrujące (decryptPrivateKey,
// decryptPackage, decryptFile) biorą materiał prywatny. Zastępuje mirror kluczy profilu
// (utils/activeProfileKeys.ts, który jest od tej pory cienką nakładką na ten moduł).
//
// Powód istnienia mirrora jest niezmieniony: te utile są zwykłymi funkcjami wołanymi poza
// kontekstem Reacta (i w Web Workerach, gdzie nie ma localStorage), więc klucze muszą do
// nich docierać przez moduł, a nie przez propsy.
//
// DWA SLOTY (docs/model-kluczy-firmowych.md, sekcja 2 i rozstrzygnięcie 7):
//   'company' — klucze FIRMY. W FAZIE 2 karmiony KLUCZAMI PROFILU (App.tsx →
//               setActiveProfilePrivateKeys), czyli zachowanie jest identyczne jak przed
//               zmianą. Podmiana źródła na granty klucza firmy to FAZA 3.
//   'guest'   — klucz STRONY ZEWNĘTRZNEJ (formularz kontaktowy, czat gościa). Dziś jedzie
//               okrężnie w polu `intermidatePrivateKey` pakietu z getKeys.ts; utils/guestKeys.ts
//               daje mu jawne wejście. Fallback w decryptPrivateKey ZOSTAJE (rozstrzygnięcie 7).
//
// Sloty są rozdzielone po to, żeby cykle życia się nie mieszały: zmiana profilu/firmy czyści
// wyłącznie 'company', a zmiana konwersacji wyłącznie 'guest'.

export type ContentKeySlot = "company" | "guest";

const SLOTS: ContentKeySlot[] = ["company", "guest"];

const slots: Record<ContentKeySlot, openpgp.key.Key[]> = {
  company: [],
  guest: [],
};

/**
 * Licznik zmian mirrora. Rośnie przy KAŻDYM zapisie i czyszczeniu. Workery porównują go
 * zamiast sklejać armory w odcisk palca (patrz primeContentKeys) — sklejanie kilkunastu
 * kluczy przy każdym pliku było niepotrzebnym kosztem na ścieżce deszyfracji.
 */
let generation = 0;

/** Cache postaci ASCII-armor, ważny dla jednej generacji (patrz getContentKeysSnapshot). */
let snapshotCache: IContentKeysSnapshot | null = null;

type ContentKeysListener = (generation: number) => void;
const listeners = new Set<ContentKeysListener>();

/**
 * Migawka mirrora do przesłania workerowi. `postMessage` nie przeniesie obiektów openpgp
 * (mają metody), a worker ma WŁASNĄ instancję tego modułu — bez migawki jego mirror byłby
 * pusty i deszyfracja miałaby do dyspozycji wyłącznie fallback na klucz intermediate.
 */
export interface IContentKeysSnapshot {
  generation: number;
  /** Klucze slotu 'company' w postaci ASCII-armor. */
  company: string[];
  /** Klucze slotu 'guest' w postaci ASCII-armor. */
  guest: string[];
}

const bumpGeneration = () => {
  generation += 1;
  snapshotCache = null;
  listeners.forEach((listener) => {
    try {
      listener(generation);
    } catch {
      // Subskrybent nie może wywrócić zapisu kluczy.
    }
  });
};

/** Ustawia klucze jednego slotu (podmiana, nie dołożenie). */
export const setContentKeys = (
  slot: ContentKeySlot,
  keys: openpgp.key.Key[] | null | undefined,
): void => {
  slots[slot] = keys ?? [];
  bumpGeneration();
};

/**
 * Klucze prywatne treści. Bez argumentu — UNIA obu slotów (tak widzi je openpgp.decrypt,
 * który i tak dobiera właściwy klucz po ID odbiorcy z szyfrogramu). Z argumentem — jeden slot.
 * Pusta tablica = mirror niezaprimowany; wołający ma wtedy fallback na klucz intermediate.
 */
export const getContentKeys = (slot?: ContentKeySlot): openpgp.key.Key[] =>
  slot ? slots[slot] : [...slots.company, ...slots.guest];

/**
 * Klucze treści w postaci ASCII-armor (unia obu slotów).
 *
 * Klucze w mirrorze są już odblokowane, więc `armor()` zapisuje je bez hasła (zweryfikowane
 * na openpgp v4: po armor+read `isDecrypted()` dalej daje true i klucz odszyfrowuje).
 * Materiał nie opuszcza przeglądarki — leci wyłącznie do własnego workera tego samego origin.
 */
export const getContentKeysArmored = (slot?: ContentKeySlot): string[] => {
  const snapshot = getContentKeysSnapshot();
  if (slot) return snapshot[slot];
  return [...snapshot.company, ...snapshot.guest];
};

const armorAll = (keys: openpgp.key.Key[]): string[] =>
  keys
    .map((key) => {
      try {
        return key.armor();
      } catch {
        // Klucz nie do zapisania — pomijamy, reszta może pasować do szyfrogramu.
        return null;
      }
    })
    .filter((armored): armored is string => !!armored);

/** Migawka mirrora dla workera. Cache'owana per generacja — armor() nie jest darmowy. */
export const getContentKeysSnapshot = (): IContentKeysSnapshot => {
  if (snapshotCache && snapshotCache.generation === generation)
    return snapshotCache;

  snapshotCache = {
    generation,
    company: armorAll(slots.company),
    guest: armorAll(slots.guest),
  };

  return snapshotCache;
};

/** Czyści jeden slot albo (bez argumentu) cały mirror — np. przy wylogowaniu. */
export const clearContentKeys = (slot?: ContentKeySlot): void => {
  if (slot) {
    slots[slot] = [];
  } else {
    SLOTS.forEach((s) => {
      slots[s] = [];
    });
  }
  bumpGeneration();
};

/** Numer generacji mirrora — odcisk palca stanu dla workerów i cache'ów. */
export const getContentKeysGeneration = (): number => generation;

/**
 * Subskrypcja zmian mirrora. Konsument docelowy (F3): unieważnianie cache'u DECRYPTED_KEYS
 * w decryptPackage po podmianie kluczy firmy. Zwraca funkcję odpinającą.
 */
export const onContentKeysChanged = (
  listener: ContentKeysListener,
): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
